import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlassCard } from '@/components/ui/layout/card';
import { CardContent } from '@/components/ui/layout/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form/input';
import { Label } from '@/components/ui/form/label';
import { Mail, Lock, HelpCircle, Shield, AlertCircle, Eye, EyeOff, CheckSquare, Square } from 'lucide-react';
import { signupWithEmail } from '@/lib/auth';
import { initiateGoogleConnect, initiateStravaConnect } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isNative } from '@/lib/platform';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toast } from '@/hooks/use-toast';
import { trackSignupStarted, trackSignupCompleted } from '@/lib/posthog';
import { Logo } from '@/components/Logo';
import { Alert, AlertDescription } from '@/components/ui/feedback/alert';
import { log } from '@/lib/logger';

export default function Signup() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  const handleGoogleSignup = async () => {
    try {
      trackSignupStarted('google');
      await initiateGoogleConnect();
    } catch (err) {
      const msg = extractErrorMessage(err, t('signup.errors.googleFailed'));
      log.error('AUTH', 'Google connection error:', msg, err);
      setError(msg);
    }
  };

  const handleStravaSignup = async () => {
    try {
      trackSignupStarted('strava');
      await initiateStravaConnect();
    } catch (err) {
      const msg = extractErrorMessage(err, t('signup.errors.stravaFailed'));
      log.error('AUTH', 'Strava connection error:', msg, err);
      setError(msg);
    }
  };

  const handleAppleSignup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      trackSignupStarted('apple');
      const { loginWithAppleNative } = await import('@/lib/apple-auth-native');
      await loginWithAppleNative();
      trackSignupCompleted('apple');
      await refreshUser();
      navigate('/onboarding', { replace: true });
    } catch (err) {
      const msg = extractErrorMessage(err, t('signup.errors.appleFailed'));
      log.error('AUTH', 'Apple connection error:', msg, err);
      if (!msg.includes("cancelled") && !msg.includes("cancel")) {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isCapacitor = isNative();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!ageConfirmed) {
      setError(t('signup.errors.ageRequired'));
      return;
    }

    if (!email || !password || !confirmPassword) {
      setError(t('signup.errors.allFields'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('signup.errors.passwordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('signup.errors.passwordLength'));
      return;
    }

    setIsLoading(true);

    try {
      trackSignupStarted('email');
      // STEP 4: Force auth state refresh after token is stored
      await signupWithEmail(email, password);
      trackSignupCompleted('email');
      log.debug("AUTH", "Token stored, refreshing user state...");
      await refreshUser();
      log.debug("AUTH", "User state refreshed, navigating to onboarding");
      // Use replace: true to prevent back button from going back to signup
      navigate('/onboarding', { replace: true });
    } catch (err: unknown) {
      const apiError = err as { status?: number; message?: string };
      if (apiError.status === 409) {
        setError(t('signup.errors.emailExists'));
      } else if (apiError.status === 400) {
        setError(apiError.message || t('signup.errors.invalidInput'));
      } else {
        setError(apiError.message || t('signup.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100svh] flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <div className="flex items-center">
          <Logo className="h-8 w-auto" />
        </div>
        <div className="flex items-center gap-2">
          <Link to="/faq">
            <Button variant="ghost" size="sm">
              <HelpCircle className="h-4 w-4 mr-1" />
              {t('signup.faq')}
            </Button>
          </Link>
          <Link to="/privacy">
            <Button variant="ghost" size="sm">
              <Shield className="h-4 w-4 mr-1" />
              {t('signup.privacy')}
            </Button>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-[clamp(1.5rem,4vw,1.875rem)] font-bold text-primary">{t('signup.createAccount')}</h1>
            <p className="text-muted-foreground">
              {t('signup.subtitle')}
            </p>
          </div>

          <GlassCard className="border-border/50 shadow-lg">
            <CardContent className="space-y-4 pt-6">
              {/* Age & Terms confirmation — shown first so users check before social buttons */}
              <button
                type="button"
                onClick={() => {
                  setAgeConfirmed(!ageConfirmed);
                  setError(null);
                }}
                className="flex items-start gap-3 w-full text-left px-1 py-1 rounded-md hover:bg-white/[0.02] transition-colors"
              >
                {ageConfirmed ? (
                  <CheckSquare className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                ) : (
                  <Square className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                )}
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {t('signup.ageConfirm')}{' '}
                  <Link to="/terms" className="text-accent hover:underline" onClick={(e) => e.stopPropagation()}>
                    {t('signup.termsOfService')}
                  </Link>
                  {' '}{t('signup.and')}{' '}
                  <Link to="/privacy" className="text-accent hover:underline" onClick={(e) => e.stopPropagation()}>
                    {t('signup.privacyPolicy')}
                  </Link>
                </span>
              </button>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Google OAuth */}
              <Button
                className="w-full"
                variant="outline"
                size="lg"
                onClick={handleGoogleSignup}
                disabled={isLoading}

              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t('signup.continueWithGoogle')}
              </Button>

              {/* Apple Sign In — only on native iOS */}
              {isCapacitor && (
                <Button
                  className="w-full bg-black text-white hover:bg-black/90 border-none"
                  variant="outline"
                  size="lg"
                  onClick={handleAppleSignup}
                  disabled={isLoading}
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="white">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  {t('signup.continueWithApple')}
                </Button>
              )}

              {/* Strava OAuth */}
              <Button
                className="w-full bg-[#FC4C02] text-white hover:bg-[#FC4C02]/90 border-none"
                variant="outline"
                size="lg"
                onClick={handleStravaSignup}
                disabled={isLoading}
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="white">
                  <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
                </svg>
                {t('signup.continueWithStrava')}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('signup.orSignUpWithEmail')}</span>
                </div>
              </div>

              {/* Email Signup Form */}
              <form onSubmit={handleSignup} className="space-y-4">

                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t('signup.email')} <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      className="pl-10"
                      disabled={isLoading}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t('signup.password')} <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError(null);
                      }}
                      className="pl-10 pr-10"
                      disabled={isLoading}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('signup.passwordHint')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">{t('signup.confirmPassword')} <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setError(null);
                      }}
                      className="pl-10 pr-10"
                      disabled={isLoading}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t('signup.creatingAccount') : t('signup.signUp')}
                </Button>
              </form>

              <div className="text-center text-sm text-muted-foreground">
                {t('signup.alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-accent hover:underline font-medium">
                  {t('signup.signIn')}
                </Link>
              </div>
            </CardContent>
          </GlassCard>

          <p className="text-center text-sm text-muted-foreground">
            {t('signup.needHelp')}{' '}
            <Link to="/faq" className="text-accent hover:underline">
              {t('signup.checkFaq')}
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-xs text-muted-foreground">
        {t('signup.copyright', { year: new Date().getFullYear() })}
      </footer>
    </div>
  );
}
